import { router, usePlocState, useRoute, ApplicationTypeAdapter } from '@hfc/ui-core';
import { useUrlHelper } from '@/shared/infrastructure/composition/useUrlHelper';
import { computed, ComputedRef, inject, onMounted, provide } from 'vue';
import { QuoteStore } from '@/quote/infrastructure/store/QuoteStore';
import { Quote } from '@/application/shared/domain/Quote';
import { PlanStore } from '@/planList/infrastructure/store/PlanStore';
import { DentalAndVisionApplicationStore } from '@/application/shared/infrastructure/store/DentalAndVisionApplicationStore';
import { AppTypeDentalOrVision, DentalAndVisionApplication } from '@/application/shared/domain/DentalAndVisionApplication';
import { MEMBER_TYPES } from '@hfc/common/src/types/MemberTypes';
import { DentalAndVisionApplicant } from '@/application/shared/domain/DentalAndVisionApplicant';
import { UUID } from '@hfc/common/src/types/UUID';
import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';

interface DentalAndVisionApplicationInterface {
  dentalAndVisionApplicationStore: DentalAndVisionApplicationStore;
  showSectionDependentAddresses: ComputedRef<boolean>;
  showLastStepInFlow: boolean;
  isChildOnlyApplication: ComputedRef<boolean>;
  showSectionDependentNames: ComputedRef<boolean>;
  primaryApplicant: ComputedRef<DentalAndVisionApplicant>;
  spouse: ComputedRef<DentalAndVisionApplicant>;
  dependents: ComputedRef<Array<DentalAndVisionApplicant>>;
  children: ComputedRef<Array<DentalAndVisionApplicant>>;
  guardian: ComputedRef<DentalAndVisionApplicant>;
  notPrimaryApplicants: ComputedRef<Array<DentalAndVisionApplicant>>;
  notPrimaryApplicantsAddressDifferentThanPrimary: ComputedRef<Array<DentalAndVisionApplicant>>;
  application: ComputedRef<DentalAndVisionApplication>;
  handleSaveFromBackoffice: (token: string, appId: string, sectionRefs: Array<{sectionSubmit?: () => Promise<boolean>}>) => Promise<void>;
  submitApplication: (planStore: PlanStore, quoteStore: QuoteStore) => Promise<boolean>;
  saveApplicationAndContinue: (callBack: () => void) => void;
  clearAndRestartApplication: (planStore: PlanStore, quoteStore: QuoteStore) => Promise<void>;
  shouldLoadApplicationFromQuote: () => Promise<boolean>;
  getQuoteFromApplication: () => Quote;
  checkReQuote: (quote: {zip_code: number, county: string}) => Promise<boolean>;
  shouldLoadApplicationFromLocalStorage: () => boolean;
}

export const useDentalAndVisionApplication = (applicationType: AppTypeDentalOrVision, uuid?: UUID, planStore?: PlanStore, quoteStore?: QuoteStore): DentalAndVisionApplicationInterface => {
  let dentalAndVisionApplicationStore = inject<DentalAndVisionApplicationStore | null>('DentalAndVisionApplicationStore') as DentalAndVisionApplicationStore;

  if (!dentalAndVisionApplicationStore) {
    dentalAndVisionApplicationStore = new DentalAndVisionApplicationStore();
    provide('DentalAndVisionApplicationStore', dentalAndVisionApplicationStore);
  }

  const dentalAndVisionApplicationState = usePlocState(dentalAndVisionApplicationStore);

  const { getRouteNameByAppType } = useUrlHelper();
  const { handleSfApiError } = useHandleApiError();

  const route = useRoute();

  const showLastStepInFlow = true;

  const application = computed(() => dentalAndVisionApplicationState.value[applicationType] as DentalAndVisionApplication);

  const primaryApplicant = computed(() => application.value.applicants.find((applicant) => applicant.type === MEMBER_TYPES.APPLICANT) as DentalAndVisionApplicant);
  const spouse = computed(() => application.value.applicants.find((applicant) => applicant.type === MEMBER_TYPES.SPOUSE) as DentalAndVisionApplicant);
  const dependents = computed(() => application.value.applicants.filter((applicant) => applicant.type === MEMBER_TYPES.DEPENDENT).map((applicant, i) => ({ ...applicant, index: i }) as DentalAndVisionApplicant));
  const children = computed(() => application.value.applicants.filter((applicant) => applicant.type === MEMBER_TYPES.CHILD).map((applicant, i) => ({ ...applicant, index: i }) as DentalAndVisionApplicant));
  const guardian = computed(() => application.value.applicants.find((applicant) => applicant.type === MEMBER_TYPES.GUARDIAN) as DentalAndVisionApplicant);
  const notPrimaryApplicants = computed(() => application.value.applicants.filter((applicant) => applicant.type !== MEMBER_TYPES.APPLICANT));
  const notPrimaryApplicantsAddressDifferentThanPrimary = computed(() => notPrimaryApplicants.value.filter((applicant) => !applicant.is_address_same_as_main));

  const isChildOnlyApplication = computed(() => dentalAndVisionApplicationStore.isChildOnlyApplication(application.value));
  const showSectionDependentNames = computed(() => application.value.applicants.length > 1 && !isChildOnlyApplication.value);
  const showSectionDependentAddresses = computed(() => notPrimaryApplicantsAddressDifferentThanPrimary.value.length > 0 && !isChildOnlyApplication.value);

  async function handleSaveFromBackoffice (token: string, appId: string, sectionRefs: Array<{sectionSubmit?: () => Promise<boolean>}>): Promise<void> {
    const isValid = await validateSections(sectionRefs);

    if (!isValid) {
      window.parent.postMessage({ type: 'applicationValidationFailed', id: ApplicationTypeAdapter.toLegacy(applicationType) }, '*');

      return;
    }

    const result = await dentalAndVisionApplicationStore.saveUpdatedApplication(applicationType, token, appId);
    window.parent.postMessage({ type: result.success ? 'applicationSaved' : 'applicationNotSaved', id: ApplicationTypeAdapter.toLegacy(applicationType) }, '*');
  }

  async function validateSections (sectionRefs: Array<{sectionSubmit?: () => Promise<boolean>}>): Promise<boolean> {
    return await Promise.all(sectionRefs.map(async (sectionRef) => {
      if (!sectionRef.sectionSubmit) {
        return true;
      }
      const result = await sectionRef.sectionSubmit();

      return typeof result === 'boolean' ? result : true;
    })).then(results => {
      return results.every(Boolean);
    });
  }

  async function submitApplication (planStore: PlanStore, quoteStore: QuoteStore): Promise<boolean> {
    const response = await dentalAndVisionApplicationStore.submitApplication(applicationType);
    if (response.error) {
      handleSfApiError(response.error);

      return false;
    }

    if (response.success && response.data) {
      const uuid = response.data;
      await removeLocalStorage(planStore, quoteStore, false);
      window.location.href = `${import.meta.env.VITE_APP_BASE_URL}/payment/new/${uuid}/application_${applicationType}`;

      return true;
    }

    return false;
  }

  async function clearAndRestartApplication (planStore: PlanStore, quoteStore: QuoteStore): Promise<void> {
    const quoteRouteName = getRouteNameByAppType(applicationType, quoteStore.state[applicationType].isSenior ? 'QuoteSenior' : 'Quote');
    await removeLocalStorage(planStore, quoteStore);
    router.push({ name: quoteRouteName });
  }

  async function removeLocalStorage (planStore: PlanStore, quoteStore: QuoteStore, resetStore: boolean = true): Promise<void> {
    await quoteStore.resetLocalStorage(applicationType);
    await planStore.resetLocalStorage(applicationType);

    if (resetStore) {
      await dentalAndVisionApplicationStore.resetLocalStorage(applicationType);
    } else {
      await dentalAndVisionApplicationStore.resetLocalStorageOnly(applicationType);
    }

    return;
  }

  const getQuoteFromApplication = (): Quote => {
    return {
      applicants: application.value.applicants.map((applicant, i) => {
        return {
          ...applicant,
          original_index: i
        };
      }),
      type: application.value.type,
      zip_code: primaryApplicant.value.main_address?.zip_code,
      county: primaryApplicant.value.main_address?.county,
      effective_date: application.value.effective_date
    } as Quote;
  };

  const shouldLoadApplicationFromLocalStorage = (): boolean => {
    return !!localStorage.getItem(`${applicationType}Application`);
  };

  const saveApplicationAndContinue = (callBack: () => void): void => {
    dentalAndVisionApplicationStore.saveApplicationOnLocalStorage(application.value, applicationType);
    callBack();
  };

  const shouldLoadApplicationFromQuote = async (): Promise<boolean> => {
    const saveAndExit = route.query.saveAndExit === 'true';

    if (saveAndExit) {
      await router.replace({ query: { ...route.query, saveAndExit: undefined } });
    }

    return !saveAndExit;
  };

  const checkReQuote = async (quote: {zip_code: number, county: string}): Promise<boolean> => {
    if (dentalAndVisionApplicationState.value.newPlanSelected === false || quote.zip_code !== primaryApplicant.value.main_address?.zip_code || quote.county.toLowerCase() !== primaryApplicant.value.main_address?.county.toLowerCase()) {
      await dentalAndVisionApplicationStore.setShowReselectPlanModal(true);
      await dentalAndVisionApplicationStore.setNewPlanSelected(false);

      return true;
    }

    return false;
  };

  onMounted(async () => {
    if (uuid) {
      const response = await dentalAndVisionApplicationStore.getApplication(applicationType, uuid);

      if (response.error) {
        return handleSfApiError(response.error);
      }

      if (quoteStore) {
        await quoteStore.initQuoteFromApplication(application.value, applicationType);
      }

      if (planStore) {
        await planStore.updateSelectedPlan(application.value.plan, applicationType);
      }

      dentalAndVisionApplicationStore.saveApplicationOnLocalStorage(application.value, applicationType);

      router.push({ name: getRouteNameByAppType(applicationType, 'Application') });
    }
  });

  return {
    handleSaveFromBackoffice,
    dentalAndVisionApplicationStore,
    showSectionDependentAddresses,
    showSectionDependentNames,
    isChildOnlyApplication,
    primaryApplicant,
    showLastStepInFlow,
    spouse,
    dependents,
    children,
    notPrimaryApplicants,
    notPrimaryApplicantsAddressDifferentThanPrimary,
    guardian,
    application,
    submitApplication,
    saveApplicationAndContinue,
    clearAndRestartApplication,
    shouldLoadApplicationFromQuote,
    getQuoteFromApplication,
    checkReQuote,
    shouldLoadApplicationFromLocalStorage
  };
};
